import {
  Box,
  Divider,
  Flex,
  Heading,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { captureException } from "@sentry/nextjs";
import { Fragment, useEffect, useState } from "react";

import { DetailData } from "@ud/api/newPhpApi/__generated__/newPhpApi.generated";
import IconInfoLarge from "components/Chakra/Icons/InfoLarge";
import Price from "components/Price";
import RentalPrice from "components/RentalPrice";
import useApiEndpoint from "hooks/useApiEndpoint";
import { useDetailState } from "scenes/OfferDetail";
import { IMarginProps } from "src/types/types";
import GetOfferInterestApiEndpoint from "utils/offer/api/GetOfferInterest";
import InfoBanner from "../InfoBanner";

type DiffVal = {
  average: number;
  diff: number;
};

const Pricing: React.FC<IMarginProps> = ({ ...margins }) => {
  const { callEndpoint: getOfferInterestApi } = useApiEndpoint(
    GetOfferInterestApiEndpoint,
  );
  const [interestCount, setInterestCount] = useState(0);
  const [diffValues, setDiffValues] = useState<DiffVal>({
    average: 0,
    diff: 0,
  });

  const { offer, isLoaded } = useDetailState();

  useEffect(() => {
    if (!offer) {
      return;
    }

    if (offer.rentalPrice && offer.priceMedian) {
      setDiffValues({
        average: offer.priceMedian.value,
        diff: offer.priceMedian.value - offer.rentalPrice.value,
      });
    }

    const callOfferInterestApi = async (passOffer: DetailData) => {
      try {
        const { data } = await getOfferInterestApi({
          offerId: passOffer.id ?? 0,
        });

        if (data) {
          setInterestCount(data.interestCount);
        }
      } catch (err) {
        captureException(err);
      }
    };

    callOfferInterestApi(offer);
  }, [offer]);

  let infoBanner = null;
  if (diffValues.diff > 0) {
    infoBanner = (
      <Box mt="20px">
        <InfoBanner icon={<IconInfoLarge boxSize="20px" />}>
          <Text variant="small_2">
            Průměrná cena v této lokalitě je{" "}
            <Price
              value={diffValues.average}
              dataTest="offerDetail.averagePrice"
            />
            . Tento pronájem je o{" "}
            <b>
              <Price
                value={diffValues.diff}
                dataTest="offerDetail.averagePrice.diff"
              />
            </b>{" "}
            levnější.
          </Text>
        </InfoBanner>
      </Box>
    );
  }
  if (interestCount > 1) {
    infoBanner = (
      <Box mt="20px" data-test="offerDetail.interest">
        <InfoBanner icon={<IconInfoLarge boxSize="20px" />}>
          <Text variant="small_2" data-test="offerDetail.interest.text">
            <b>
              {interestCount}{" "}
              {interestCount >= 5 ? "lidí se zajímá" : "lidi se zajímájí"}
            </b>{" "}
            o tuto nabídku
          </Text>
        </InfoBanner>
      </Box>
    );
  }

  return (
    <Box {...margins}>
      <Stack direction="column" justify="flex-start" spacing="4px">
        <Skeleton isLoaded={isLoaded} minH="28px">
          <Heading
            color="ud-grey-600"
            as="h2"
            textAlign="left"
            lineHeight="28px"
            data-test="offerDetail.price"
          >
            {offer?.rentalPrice?.value ? (
              <RentalPrice
                rentalPrice={offer.rentalPrice}
                isDetailPrice
                offerTypeId={offer.offerTypeId}
              />
            ) : (
              <Text as="span" fontSize="inherit">
                Cena u makléře
              </Text>
            )}
          </Heading>
        </Skeleton>
        {offer?.priceNote && (
          <Skeleton isLoaded={isLoaded}>
            <Text lineHeight="20px" data-test="offerDetail.price.note">
              {offer.priceNote}
            </Text>
          </Skeleton>
        )}
        {offer?.priceParameters?.length && (
          <Skeleton isLoaded={isLoaded}>
            <Stack mt="20px">
              {offer.priceParameters.map((param, i) => (
                <Fragment key={i}>
                  <Flex justify="space-between">
                    <Text>{param.title}</Text>
                    <Text fontWeight="bold">{param.value}</Text>
                  </Flex>
                  {offer?.priceParameters &&
                    offer.priceParameters.length - 1 > i && <Divider />}
                </Fragment>
              ))}
            </Stack>
          </Skeleton>
        )}
      </Stack>
      {infoBanner}
    </Box>
  );
};

export default Pricing;
